/**
 * One-shot, once per machine: put the Spending Pace pane (`wosvPace`) under
 * the Spending pane (`wosvSpending`) in the Wealth Overview's saved
 * arrangements, which were seeded before it existed (the owner, 2026-10-14:
 * "own pane under spending"). The page seed is idempotent by name, so an
 * Overview that already exists never takes a new pane on its own.
 *
 * The Spending pane's place becomes a column of two: Spending, then Spending
 * Pace. A column nobody dragged starts its first row at the 380px band, which
 * would shrink Spending; so the new column's first row is written at
 * `SPENDING_ROW_PX`, in each place a drag would have written it (the shared
 * layout and each Display Profile's own), and Spending Pace takes the rest.
 *
 * A tree that already holds `wosvPace`, or holds no `wosvSpending`, is left
 * alone, so a reader who removed the pane keeps it removed. The flag is not an
 * `mpp:` key: it is this machine's, because each machine holds its own
 * arrangements, and it is not synced.
 */
import { rawGet, rawSet } from "@/lib/display/layoutNamespace";
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safeLocalStorage";
import type { WsNode, WorkspacePage } from "@/lib/workspace/workspacePages";

const FLAG = "migrate:wealth-overview-spending-pace:v1";
const LIST_KEY = "mpp:workspaces:v1";
const PAGE_NAMES = new Set(["wealth overview · verdict", "wealth overview · verdict · wide"]);
/** The pane's panel: PanelWorkspacePage's `${storageKey}:ws`, as the nested workspace names its row heights. */
const PANEL_ID = "mpp:wealth-overview-preview:pw:v1:ws";
const DP_SEP = "\u0000@dp:";
export const SPENDING = "wosvSpending";
export const PACE = "wosvPace";
/**
 * The Spending pane's starting height above the new pane, the seeds' own
 * figure: its finding and its 24 months of bars (its donut and table scroll
 * inside it, as they did below the bars before). The owner's desk window is
 * 1080px tall, where a column of the wide Overview is about 600px: at 620 the
 * new pane was below the fold of its column (measured 2026-10-14).
 */
export const SPENDING_ROW_PX = 330;

function nid(prefix: string): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function treeHas(node: WsNode, id: string): boolean {
  if (node.t === "leaf") return node.component === id;
  return node.children.some((c) => treeHas(c, id));
}

/**
 * The tree with the Spending leaf made a column of Spending over Spending
 * Pace, and the new column's id; null when there is nothing to do (no
 * Spending, or Spending Pace already in it). The first Spending leaf found is
 * the one. Pure: `ids` makes the new leaves' ids.
 *
 * The column keeps the leaf's id and Spending takes a new one inside it. A
 * row of columns keeps its dragged widths by the ids of its panels: a column
 * with a new id was a row the library had no widths for, so the owner's
 * widths went back to the seed's and the row's new layout was written as an
 * edit, which opened the page on "Unsaved changes" (seen in a headless
 * browser, 2026-10-14).
 */
export function withPaceUnderSpending(root: WsNode, ids: (prefix: string) => string = nid): { root: WsNode; stackId: string } | null {
  if (treeHas(root, PACE) || !treeHas(root, SPENDING)) return null;
  let stackId: string | null = null;
  const walk = (node: WsNode): WsNode => {
    if (stackId) return node;
    if (node.t === "leaf") {
      if (node.component !== SPENDING) return node;
      stackId = node.id;
      return { id: node.id, t: "split", dir: "v", children: [{ ...node, id: ids("l") }, { id: ids("l"), t: "leaf", component: PACE }] };
    }
    return { ...node, children: node.children.map(walk) };
  };
  const next = walk(root);
  return stackId ? { root: next, stackId } : null;
}

const rowKey = (pageId: string, stackId: string) => `mpp:ws:rowh:nested-ws:${PANEL_ID}:${pageId}:${stackId}:v1`;

export function migrateOverviewSpendingPace(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(FLAG) === "1") return;
  } catch {
    return;
  }
  const heights = JSON.stringify([SPENDING_ROW_PX]);

  let pool: WorkspacePage[] = [];
  try {
    const raw = safeLocalStorageGet(LIST_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as WorkspacePage[];
      if (Array.isArray(arr)) pool = arr;
    }
  } catch {
    pool = [];
  }

  const pageIds = new Set<string>();
  let poolChanged = false;
  const nextPool = pool.map((page) => {
    if (!page?.root || typeof page.name !== "string" || !PAGE_NAMES.has(page.name.trim().toLowerCase())) return page;
    pageIds.add(page.id);
    const moved = withPaceUnderSpending(page.root);
    if (!moved) return page;
    poolChanged = true;
    // The shared tree is what a profile with no tree of its own draws, with the shared row heights.
    rawSet(rowKey(page.id, moved.stackId), heights);
    return { ...page, root: moved.root, updatedAt: Date.now() };
  });
  if (poolChanged) safeLocalStorageSet(LIST_KEY, JSON.stringify(nextPool));

  // Each Display Profile's own tree of those pages, and the shared override: each written where it was read.
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith("mpp:ws-tree:")) keys.push(key);
    }
    for (const key of keys) {
      const [logical, profile] = key.split(DP_SEP);
      const pageId = [...pageIds].find((id) => logical === `mpp:ws-tree:${id}:v1`);
      if (!pageId) continue;
      const raw = rawGet(key);
      if (!raw) continue;
      const root = JSON.parse(raw) as WsNode;
      if (!root || (root.t !== "leaf" && root.t !== "split")) continue;
      const moved = withPaceUnderSpending(root);
      if (!moved) continue;
      const suffix = profile != null ? `${DP_SEP}${profile}` : "";
      rawSet(key, JSON.stringify(moved.root));
      rawSet(`${rowKey(pageId, moved.stackId)}${suffix}`, heights);
    }
  } catch {
    /* the shared tree's change still stands */
  }

  try {
    window.localStorage.setItem(FLAG, "1");
  } catch {
    /* quota: it runs again next time, and finds nothing to do */
  }
}
